/**
 * RXNT Integration SDK loader (https://rxnt.github.io/rxnt-integration-sdk/).
 *
 * Replaces manual portal login (auth.rxnt.com). Launch flow:
 * 1. Load sdk.min.js → registers <rxnt-launch-button>
 * 2. authFactory → Snap API GET /rxnt/launch/token (machine credentials, server-side only)
 * 3. launchDataFactory → Snap API GET /rxnt/launch/context → SDK POSTs to {baseUrl}/ehr-gateway/launch
 *
 * Env (required in .env): REACT_APP_RXNT_SDK_BASE, REACT_APP_RXNT_SDK_VERSION
 * @see https://rxnt.github.io/rxnt-integration-sdk/
 */
let sdkLoadPromise = null;

function getRxntSdkBase() {
  const base = String(process.env.REACT_APP_RXNT_SDK_BASE || '').trim().replace(/\/$/, '');
  if (!base) {
    throw new Error(
      'REACT_APP_RXNT_SDK_BASE is not set. Add it to .env (e.g. https://cdn.rxnt.com/rxnt-integration).'
    );
  }
  return base;
}

function getRxntSdkVersion() {
  const version = String(process.env.REACT_APP_RXNT_SDK_VERSION || '').trim();
  if (!version) {
    throw new Error(
      'REACT_APP_RXNT_SDK_VERSION is not set. Add it to .env (e.g. 1.0.4; avoid 1.0.0 — CDN 404).'
    );
  }
  return version;
}

export function getRxntSdkScriptUrl() {
  return `${getRxntSdkBase()}/${getRxntSdkVersion()}/sdk.min.js`;
}

/**
 * Load RXNT Integration SDK once (registers <rxnt-launch-button> custom element).
 */
export function loadRxntLaunchSdk() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('RXNT SDK can only load in the browser'));
  }

  if (window.customElements?.get('rxnt-launch-button')) {
    return Promise.resolve();
  }

  if (sdkLoadPromise) {
    return sdkLoadPromise;
  }

  sdkLoadPromise = new Promise((resolve, reject) => {
    const src = getRxntSdkScriptUrl();
    const existing = document.querySelector(`script[data-rxnt-launch-sdk="${src}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener(
        'error',
        () => reject(new Error(`Failed to load RXNT SDK from ${src}`)),
        { once: true }
      );
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.dataset.rxntLaunchSdk = src;
    script.onload = () => {
      if (window.customElements?.get('rxnt-launch-button')) {
        resolve();
        return;
      }
      reject(
        new Error(
          `RXNT SDK loaded from ${src} but rxnt-launch-button was not registered`
        )
      );
    };
    script.onerror = () =>
      reject(
        new Error(
          `Failed to load RXNT SDK from ${src}. Check REACT_APP_RXNT_SDK_BASE and REACT_APP_RXNT_SDK_VERSION in .env.`
        )
      );
    document.head.appendChild(script);
  });

  return sdkLoadPromise;
}

/** Where RXNT sends the user after EHR session when no patient-specific return is built. */
export function defaultRxntLaunchReturnUrl() {
  if (typeof window === 'undefined') return '';
  return `${window.location.origin}${window.location.pathname}${window.location.search || ''}`;
}
